import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@cbhq/cds-web/layout';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { Avatar } from '@cbhq/cds-web/media/Avatar';
import { TextHeadline, TextLabel1, TextLabel2 } from '@cbhq/cds-web/typography';
import { TextLegal } from '@cbhq/cds-web/typography/TextLegal';
import { ActionType, ComponentType } from '@cbhq/client-analytics';

import { navigateWithConditionalReplace } from '../../../utils/navigationUtils';
import { NIL_UUID } from '../../constants';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useManageAccountNavigation } from '../../hooks/useManageAccountNavigation';
import { useClientProps } from '../../providers/ClientPropsProvider';
import { ProfileType } from '../../types';
import { handleProfileExpired, isExpired } from '../../utils/profile';
import { ManageAccountCTA } from '../ManageAccountCTA';

import { fallbackColor } from './AvatarStack';
import { ItemContainer } from './ItemContainer';

type ProfileProps = ProfileType & {
  index: number;
  disabled: boolean;
};

const messages = defineMessages({
  primaryAccountLabel: {
    defaultMessage: 'Other accounts',
    id: 'primary profile name {name} and email {email}',
    description: 'Label for primary accounts in the user switcher menu.',
  },
  otherAccountLabel: {
    defaultMessage: 'Other accounts',
    id: 'click to switch profile name {name} and email {email}',
    description: 'Label for other accounts in the user switcher menu.',
  },
});

export function SelectedProfile({ name = '', email, avatarUrl }: ProfileType) {
  const { formatMessage } = useIntl();
  const handlePress = useManageAccountNavigation();

  // 108px is the deliberate baseline for name + email + ManageAccountCTA.
  // The managed-by line now renders separately via ManagedByBanner, above
  // this row, so it no longer needs extra height here.
  const height = useMemo(() => (!name && !email ? undefined : '108px'), [name, email]);

  return (
    <ItemContainer
      height={height}
      handleOnClick={handlePress}
      accessibilityLabel={formatMessage(messages.primaryAccountLabel, { name, email })}
    >
      <HStack gap={2}>
        <Box
          width="32px"
          height="32px"
          justifyContent="center"
          alignItems="center"
          paddingTop={1.5}
        >
          <Avatar src={avatarUrl} alt={name} name={name} colorScheme="blue" selected />
        </Box>
        <VStack className="sensitive-data">
          {name && (
            <TextHeadline as="p" overflow="break" numberOfLines={1} display="block">
              {name}
            </TextHeadline>
          )}
          {email && (
            <TextLegal as="p" overflow="break" numberOfLines={1} color="fgMuted" display="block">
              {email}
            </TextLegal>
          )}
          <ManageAccountCTA />
        </VStack>
      </HStack>
    </ItemContainer>
  );
}

export function Profile({
  name = '',
  email,
  avatarUrl,
  expiresAt,
  index,
  userUuid,
  disabled,
  profileId,
}: ProfileProps) {
  const { config, onBeforeSwitchAccount } = useClientProps();
  const { logEvent } = useAnalytics();
  const { formatMessage } = useIntl();

  const handleSwitchUser = useCallback(async () => {
    const expired = isExpired(expiresAt);

    if (!expired) {
      try {
        await onBeforeSwitchAccount?.({
          targetUUID: userUuid ?? NIL_UUID,
          isTargetSessionExpired: false,
        });
      } catch (error) {
        logEvent('user_switcher_switch_account_callback_failed', {
          action: ActionType.click,
          componentType: ComponentType.button,
          actionType: 'switch_profile_not_expired',
          error: error instanceof Error ? error.message : 'unknown error',
          targetProfile: userUuid ?? NIL_UUID,
        });
      }

      logEvent('user_switcher_switch_profile', {
        action: ActionType.click,
        componentType: ComponentType.button,
        actionType: 'switch_profile_not_expired',
        targetProfile: userUuid ?? NIL_UUID,
      });

      const redirect = `${config?.loginUrl ?? '/login'}?authuser=${encodeURIComponent(
        profileId ?? NIL_UUID,
      )}&action=switch_account&expired=false`;

      // If it is inside of an iframe should postMessage instead of change location href
      if (window.self !== window.top) {
        window.parent.postMessage({ redirect }, '*');
      } else {
        navigateWithConditionalReplace(redirect);
      }
    } else {
      try {
        await onBeforeSwitchAccount?.({
          targetUUID: userUuid ?? NIL_UUID,
          isTargetSessionExpired: true,
        });
      } catch (error) {
        logEvent('user_switcher_switch_account_callback_failed', {
          action: ActionType.click,
          componentType: ComponentType.button,
          actionType: 'switch_profile_is_expired',
          error: error instanceof Error ? error.message : 'unknown error',
          targetProfile: userUuid ?? NIL_UUID,
        });
      }

      logEvent('user_switcher_switch_profile', {
        action: ActionType.click,
        componentType: ComponentType.button,
        actionType: 'switch_profile_is_expired',
        targetProfile: userUuid,
      });
      handleProfileExpired(email, config?.loginUrl);
    }
  }, [config?.loginUrl, email, expiresAt, logEvent, onBeforeSwitchAccount, profileId, userUuid]);

  return (
    <ItemContainer
      handleOnClick={handleSwitchUser}
      accessibilityLabel={formatMessage(messages.otherAccountLabel, { name, email })}
      disabled={disabled}
    >
      <Box width="32px" height="32px" justifyContent="center" alignItems="center">
        <Avatar src={avatarUrl} alt={name} name={name} colorScheme={fallbackColor(index)} />
      </Box>
      <VStack>
        <TextLabel1 as="p" overflow="break" numberOfLines={1} color="fg" display="block">
          {email}
        </TextLabel1>
        <TextLabel2 as="p" overflow="break" numberOfLines={1} color="fgMuted" display="block">
          {name}
        </TextLabel2>
      </VStack>
    </ItemContainer>
  );
}
